/**
 * Owns the one comments panel a Documents page needs, plus the viewer it
 * opens a document in from a card's page badge or a deep link. Rows call
 * `useDocumentCommentsPanel()?.open(...)` instead of each rendering a panel —
 * virtualised rows unmount as you scroll, which would tear an open panel down.
 *
 * Mounted ONCE, by `pages/BuildingDocuments`, above every view tab: that mount
 * also owns the building-scoped realtime channel every comments consumer on
 * the page (rail, panel, row badges) shares through react-query.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { toast } from "sonner";

import { useDocumentPreview } from "@/components/chat/SidebarChat/DocLink/useDocumentPreview";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useDocumentCommentsRealtime } from "@/stores/documents/comments/queries";
import { lookupThread } from "@/stores/documents/comments/repository";
import { getDocumentById } from "@/stores/documents/repository";
import { useMentionsStore } from "@/stores/pm/hooks/usePMStore";
import { canPreviewFile } from "@/utils/previewUtils";
import {
  DocumentCommentsPanelContext,
  type DocumentCommentsPanelApi,
  type DocumentCommentsPanelTarget,
} from "../../comments/documentCommentsPanelContext";
import { DocumentViewerDialog } from "../DocumentViewerDialog";
import { DocumentCommentsPanel } from "./DocumentCommentsPanel";

interface ViewerIntent {
  threadId: number | null;
  page: number | null;
}

export function DocumentCommentsPanelProvider({
  children,
  buildingId,
  realtime = false,
}: {
  children: ReactNode;
  buildingId: number | null;
  /** Subscribe this mount to the building's comment events. One per page. */
  realtime?: boolean;
}) {
  const { t } = useLanguage();
  const mentionsStore = useMentionsStore();
  const [target, setTarget] = useState<DocumentCommentsPanelTarget | null>(null);
  const [intent, setIntent] = useState<ViewerIntent | null>(null);
  const preview = useDocumentPreview();

  useDocumentCommentsRealtime(realtime ? buildingId : null, {
    onChange: () => void mentionsStore.refreshDocuments(),
  });

  const { error: previewError, clearError: clearPreviewError } = preview;
  const threadUnavailable = t.documents.comments.threadUnavailable;
  useEffect(() => {
    if (!previewError) return;
    toast.error(previewError);
    clearPreviewError();
  }, [previewError, clearPreviewError]);

  const openDocument = useCallback(
    async (documentId: number, page: number | null, threadId: number | null) => {
      const doc = await getDocumentById(String(documentId));
      if (!doc || doc.is_deleted || doc.building_id == null) {
        toast.error(threadUnavailable);
        return;
      }
      if (!canPreviewFile(doc.filename, doc.content_type)) {
        // No preview for this file type: the panel is the only surface.
        setTarget({
          documentId: doc.id,
          buildingId: doc.building_id,
          filename: doc.filename,
          initialThreadId: threadId,
        });
        return;
      }
      setTarget(null);
      setIntent({ threadId, page });
      await preview.openPreviewByDoc({
        id: doc.id,
        filename: doc.filename,
        file_path: doc.file_path,
        building_id: doc.building_id,
        content_type: doc.content_type,
        size: doc.size,
        created_at: doc.created_at,
        parent_document_id: doc.parent_document_id,
        version: doc.version ?? undefined,
      });
    },
    [preview, threadUnavailable]
  );

  const api = useMemo<DocumentCommentsPanelApi>(
    () => ({
      open: setTarget,
      openThread: async (threadId) => {
        const row = await lookupThread(threadId);
        if (!row || row.deleted_at !== null) {
          toast.error(threadUnavailable);
          return;
        }
        await openDocument(row.document_id, row.page_number, threadId);
      },
      openDocumentAtPage: (documentId, page) => openDocument(documentId, page, null),
    }),
    [openDocument, threadUnavailable]
  );

  return (
    <DocumentCommentsPanelContext.Provider value={api}>
      {children}
      <DocumentCommentsPanel
        target={target}
        onClose={() => setTarget(null)}
        onOpenDocument={(documentId, page) => void openDocument(documentId, page, null)}
      />
      <DocumentViewerDialog
        document={preview.document}
        blobUrl={preview.blobUrl}
        blobData={preview.blobData}
        open={preview.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            preview.closePreview();
            setIntent(null);
          }
        }}
        originalDwgFilePath={preview.originalDwgFilePath ?? undefined}
        commentsDefaultOpen={intent != null}
        commentsInitialThreadId={intent?.threadId ?? null}
        initialPage={intent?.page ?? undefined}
      />
    </DocumentCommentsPanelContext.Provider>
  );
}
